/**
 * Session Capture publish action (Sprint 3.4 REDO).
 *
 * Serialises the current PROM-instrument selection + schedule to a
 * synthetic `EditablePathway`, then runs `pathwayToPlanDefinition` to
 * build a FHIR `PlanDefinition`. The result lands on the FHIR client
 * via `createPlanDefinition`. An AuditEvent with `source =
 * session-capture-publish` is emitted alongside per HC9.
 *
 * Why a synthetic pathway: the prototype publish target is the canonical
 * TLP1 PlanDefinition. We derive the action set from the selected
 * instruments so the publish payload reflects the live selection and
 * the existing mapper round-trip stays the only source of truth for
 * PlanDefinition shape.
 *
 * HC1 reproducibility: the function is pure given (selection, schedule,
 *   tenantId, recorded). Same inputs always emit the same FHIR.
 * HC8 fail-closed: `publishSessionCapture` refuses if no instruments
 *   are selected.
 * HC9 strict registry: AuditEvent always carries `source =
 *   session-capture-publish`.
 * HC5 zero em / en dashes.
 */
import { auditPayloadToFhir } from '../../services/fhir-mappers';
import { pathwayToPlanDefinition } from '../../services/fhir-mappers';
import type { FhirClient } from '../../services/fhir';
import type {
  AuditEvent,
  PlanDefinition,
} from '../../services/fhir';
import type { EditablePathway } from '../../state/pathway-types';
import type { PathwayId } from '../../state/pathway-ids';
import { pathwayIdFor } from '../../state/pathway-ids';
import {
  ADMIN_SCHEDULE_STEPS,
  type PromInstrumentCode,
  type ScheduleAssignments,
} from './types';

export const PUBLISH_AUDIT_SOURCE = 'session-capture-publish';
export const PUBLISH_AUDIT_VERB = 'published';

export interface PublishSessionCaptureInput {
  selectedInstruments: PromInstrumentCode[];
  assignments: ScheduleAssignments;
  tenantId: string;
  /** Pathway id to publish. Defaults to `cardiometabolic-pathway` (internal id `tlp1-glp1-v1`). */
  pathwayId?: PathwayId;
  /** Practitioner reference recorded as the AuditEvent subject. */
  practitionerReference?: string;
  /** Stable timestamp for the AuditEvent (HC1 reproducibility). */
  recorded?: string;
}

export interface PublishSessionCaptureResult {
  planDefinition: PlanDefinition;
  auditEvent: AuditEvent;
}

/**
 * Build the synthetic `EditablePathway` for the publish payload. One
 * `nba` action per selected instrument so the resulting PlanDefinition
 * has at least one action (Aidbox quirk: `action` MUST NOT be empty).
 */
export function buildPublishPathway(
  input: PublishSessionCaptureInput,
): EditablePathway {
  const pathwayId = input.pathwayId ?? 'cardiometabolic-pathway';
  const id = pathwayIdFor(pathwayId);
  const stepLabelFor = (code: PromInstrumentCode): string => {
    const stepIds = ADMIN_SCHEDULE_STEPS.filter((step) =>
      input.assignments[step.id].includes(code),
    ).map((step) => step.label);
    return stepIds.length > 0 ? stepIds.join(', ') : 'unscheduled';
  };
  return {
    id,
    pathwayId,
    tenantId: input.tenantId,
    nodes: input.selectedInstruments.map((code) => ({
      id: `session-capture-${code.toLowerCase()}`,
      nodeType: 'nba' as const,
      label: `Administer ${code}`,
      description: `Schedule: ${stepLabelFor(code)}`,
    })),
    edges: input.selectedInstruments.slice(1).map((code, idx) => ({
      id: `edge-${idx}`,
      source: `session-capture-${input.selectedInstruments[idx].toLowerCase()}`,
      target: `session-capture-${code.toLowerCase()}`,
    })),
  };
}

/**
 * Run the publish flow. Throws when called with zero instruments
 * (HC8 fail-closed). The FHIR client is the caller's responsibility;
 * any error from the client (TenantMismatchError, FhirValidationError,
 * etc.) propagates to the caller for surface handling.
 */
export async function publishSessionCapture(
  client: FhirClient,
  input: PublishSessionCaptureInput,
): Promise<PublishSessionCaptureResult> {
  if (input.selectedInstruments.length === 0) {
    throw new Error(
      'publishSessionCapture: refusing to publish with zero instruments selected (HC8 fail-closed)',
    );
  }
  const pathway = buildPublishPathway(input);
  const draft = pathwayToPlanDefinition(pathway);
  const planDefinition = await client.createPlanDefinition(
    draft,
    input.tenantId,
  );
  const recorded = input.recorded ?? new Date().toISOString();
  const auditDraft = auditPayloadToFhir({
    recorded,
    source: PUBLISH_AUDIT_SOURCE,
    tenantId: input.tenantId,
    subject: {
      reference: input.practitionerReference ?? 'Practitioner/anonymous',
      display: 'Session Capture publisher',
      typeCode: 'practitioner',
    },
    verb: PUBLISH_AUDIT_VERB,
    verbDisplay: 'Published Cardiometabolic Pathway to Pathway Studio',
    object: {
      reference: `PlanDefinition/${planDefinition.id ?? pathway.id}`,
      display: pathway.pathwayId,
      typeCode: 'plan-definition',
    },
    action: 'C',
    outcome: '0',
  });
  const auditEvent = await client.createAuditEvent(auditDraft, input.tenantId);
  return { planDefinition, auditEvent };
}
